import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, once } from '@web/utils';
import { map, Observable } from 'rxjs';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  WarehouseProductControlsToDisable,
  WarehouseProductDetails,
  WarehouseProductFormData,
} from '../../models';
import { WarehouseProductDataSourceService } from '../../services/warehouse-product-data-source.service';
import { WarehouseProductFormComponent } from '../../components/warehouse-product-form/warehouse-product-form.component';

@Component({
  selector: 'app-warehouse-product-edit',
  imports: [
    CommonModule,
    WarehouseProductFormComponent,
    SpinnerFilledComponent,
    AlertsComponent,
  ],
  templateUrl: './warehouse-product-edit.component.html',
  styleUrl: './warehouse-product-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class WarehouseProductEditComponent {
  /* prettier-ignore */
  private readonly warehouseProductDataSource = inject(WarehouseProductDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public readonly id = input.required<Guid>();
  public readonly owner = input.required<string>();

  public readonly submitSuccessEvent = output();

  protected data$!: Observable<WarehouseProductFormData>;
  protected disabledControls: WarehouseProductControlsToDisable[] = [];

  public constructor() {
    effect(() => this.configureDataSource());
  }

  public handleSubmitSuccessEvent(data: WarehouseProductFormData): void {
    this.warehouseProductDataSource
      .updateByFormData(this.owner(), this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => {
          this.notifySubmitSuccessEvent();
        },
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    this.alerts.showMultiple((builder) =>
      this.httpError
        .extract(response)
        .messages()
        .map((message) => builder.buildFailure(message)),
    );
  }

  private configureDataSource(): void {
    this.data$ = this.warehouseProductDataSource
      .fetchDetailsById(this.owner(), this.id())
      .pipe(
        map((response: WarehouseProductDetails) => {
          this.disabledControls = response.canBeQuantityEdited
            ? []
            : ['quantity'];

          return new WarehouseProductFormData({
            brandId: response.brandId,
            durabilityInformation: response.durabilityInformation,
            measure: response.measure,
            promotion: response.promotion,
            quantity: response.quantity,
            unit: response.unit,
            name: response.name,
            sku: response.sku,
            type: response.type,
            ownerId: response.ownerId,
            localizationId: response.localizationId,
          });
        }),
      );

    this.changeDetectorRef.detectChanges();
  }
}
